/**
 * Filter Utilities
 *
 * Functions for applying filters to data arrays
 */

import type { FilterCondition, FilterGroup, FilterOperator } from './types';

/**
 * Normalize a value for comparison (handles string 'true'/'false' vs boolean)
 */
function normalizeForComparison(value: unknown): unknown {
  if (value === 'true')
    return true;
  if (value === 'false')
    return false;
  return value;
}

/**
 * Check if two values are equal (with boolean coercion for string 'true'/'false')
 */
function valuesEqual(a: unknown, b: unknown): boolean {
  const normalizedA = normalizeForComparison(a);
  const normalizedB = normalizeForComparison(b);
  return normalizedA === normalizedB;
}

/**
 * Apply a single filter condition to a value
 */
function applyCondition(value: unknown, operator: FilterOperator, filterValue: unknown): boolean {
  // Handle null/undefined checks first
  if (operator === 'is_null') {
    return value === null || value === undefined || value === '';
  }
  if (operator === 'is_not_null') {
    return value !== null && value !== undefined && value !== '';
  }

  // For other operators, null values don't match
  if (value === null || value === undefined) {
    return false;
  }

  switch (operator) {
    case 'equals': {
      return valuesEqual(value, filterValue);
    }

    case 'not_equals': {
      return !valuesEqual(value, filterValue);
    }

    case 'contains': {
      const strValue = String(value).toLowerCase();
      const strFilter = String(filterValue).toLowerCase();
      return strValue.includes(strFilter);
    }

    case 'not_contains': {
      const strValue = String(value).toLowerCase();
      const strFilter = String(filterValue).toLowerCase();
      return !strValue.includes(strFilter);
    }

    case 'starts_with': {
      const strValue = String(value).toLowerCase();
      const strFilter = String(filterValue).toLowerCase();
      return strValue.startsWith(strFilter);
    }

    case 'ends_with': {
      const strValue = String(value).toLowerCase();
      const strFilter = String(filterValue).toLowerCase();
      return strValue.endsWith(strFilter);
    }

    case 'gt': {
      return Number(value) > Number(filterValue);
    }

    case 'gte': {
      return Number(value) >= Number(filterValue);
    }

    case 'lt': {
      return Number(value) < Number(filterValue);
    }

    case 'lte': {
      return Number(value) <= Number(filterValue);
    }

    case 'between': {
      if (!Array.isArray(filterValue) || filterValue.length !== 2) {
        return false;
      }
      const numValue = Number(value);
      return numValue >= Number(filterValue[0]) && numValue <= Number(filterValue[1]);
    }

    case 'in': {
      if (!Array.isArray(filterValue)) {
        return valuesEqual(value, filterValue);
      }
      // Check if normalized value matches any normalized filter value
      const normalizedValue = normalizeForComparison(value);
      return filterValue.some((fv) => normalizeForComparison(fv) === normalizedValue);
    }

    case 'not_in': {
      if (!Array.isArray(filterValue)) {
        return !valuesEqual(value, filterValue);
      }
      const normalizedValue = normalizeForComparison(value);
      return !filterValue.some((fv) => normalizeForComparison(fv) === normalizedValue);
    }

    default: {
      return true;
    }
  }
}

/**
 * Apply a filter group to a single row
 */
function applyFilterGroup<T>(row: T, group: FilterGroup): boolean {
  const enabledConditions = group.conditions.filter((c) => c.enabled);

  if (enabledConditions.length === 0) {
    return true; // No filters = all rows pass
  }

  const passes = (condition: typeof enabledConditions[number]) => {
    const value = (row as Record<string, unknown>)[condition.field];
    return applyCondition(value, condition.operator, condition.value);
  };

  return group.logic === 'and'
    ? enabledConditions.every((c) => passes(c))
    : enabledConditions.some((c) => passes(c));
}

/**
 * Apply filters to an array of data
 */
export function applyFilters<T>(data: T[], filters: FilterGroup): T[] {
  return data.filter((row) => applyFilterGroup(row, filters));
}

/**
 * Create an empty filter group
 */
export function createEmptyFilterGroup(): FilterGroup {
  return {
    id: crypto.randomUUID(),
    logic: 'and',
    conditions: [],
  };
}

/**
 * Create a new filter condition
 */
export function createCondition(field: string, operator: FilterOperator = 'equals', value: unknown = ''): FilterCondition {
  return {
    id: crypto.randomUUID(),
    field,
    operator,
    value,
    enabled: true,
  };
}

/**
 * Add a condition to a filter group
 */
export function addCondition(group: FilterGroup, condition: FilterCondition): FilterGroup {
  return {
    ...group,
    conditions: [...group.conditions, condition],
  };
}

/**
 * Update a condition in a filter group
 */
export function updateCondition(group: FilterGroup, conditionId: string, updates: Partial<FilterCondition>): FilterGroup {
  return {
    ...group,
    conditions: group.conditions.map((c) =>
      c.id === conditionId ? { ...c, ...updates } : c,
    ),
  };
}

/**
 * Remove a condition from a filter group
 */
export function removeCondition(group: FilterGroup, conditionId: string): FilterGroup {
  return {
    ...group,
    conditions: group.conditions.filter((c) => c.id !== conditionId),
  };
}

/**
 * Toggle a condition's enabled state
 */
export function toggleCondition(group: FilterGroup, conditionId: string): FilterGroup {
  return {
    ...group,
    conditions: group.conditions.map((c) =>
      c.id === conditionId ? { ...c, enabled: !c.enabled } : c,
    ),
  };
}

/**
 * Count active (enabled) filters
 */
export function countActiveFilters(group: FilterGroup): number {
  return group.conditions.filter((c) => c.enabled).length;
}
